// JSON-LD schema builders for city pages.
// Emits a single @graph with LocalBusiness (+ AggregateRating + areaServed),
// FAQPage, and BreadcrumbList — the three schema types Google uses for the
// rich result types most relevant to a local home-service business.

const BUSINESS_NAME = "Pool Guru Treasure Coast";
const PHONE = "+1-772-289-0405";
const PHONE_DISPLAY = "(772) 289-0405";
const REGION = "FL";
const COUNTRY = "US";
const SERVICE_TYPE = "Pool cleaning, pool repair, green pool recovery, saltwater pool service";
// Approx geographic center of Pool Guru's service area (Port St. Lucie, FL)
const HQ_GEO = { latitude: 27.2939, longitude: -80.3503 };
const HOURS = [
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "08:00",
    closes: "17:00",
  },
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Saturday"],
    opens: "09:00",
    closes: "14:00",
  },
];

function origin() {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

function cityUrl(slug) {
  return `${origin()}/pool-service-${slug}`;
}

function buildBreadcrumb(city, parentCity) {
  const items = [
    { name: "Home", url: `${origin()}/` },
    { name: "Service Areas", url: `${origin()}/locations` },
  ];
  if (parentCity) {
    items.push({ name: parentCity.name, url: cityUrl(parentCity.slug) });
  }
  items.push({ name: city.name, url: cityUrl(city.slug) });
  return {
    "@type": "BreadcrumbList",
    "@id": `${cityUrl(city.slug)}#breadcrumb`,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

function buildFaqPage(city) {
  const faqs = city?.content?.faqs || [];
  if (!faqs.length) return null;
  return {
    "@type": "FAQPage",
    "@id": `${cityUrl(city.slug)}#faq`,
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}

function buildLocalBusiness(city, parentCity, allCities, reviews) {
  const url = cityUrl(city.slug);
  const approved = (reviews || []).filter((r) => r.is_approved);
  const ratingValue =
    approved.length > 0
      ? (approved.reduce((s, r) => s + r.rating, 0) / approved.length).toFixed(1)
      : null;

  const areaServed = [];
  // Always include the page's own city
  areaServed.push({
    "@type": "City",
    name: `${city.name}, ${REGION}`,
    sameAs: url,
  });
  // Add parent + sibling cities so Google understands the local-pack cluster
  if (parentCity) {
    areaServed.push({ "@type": "City", name: `${parentCity.name}, ${REGION}`, sameAs: cityUrl(parentCity.slug) });
  }
  // Other top-level cities in the broader Treasure Coast service footprint
  (allCities || [])
    .filter((c) => !c.parent && c.slug !== city.slug && c.slug !== parentCity?.slug)
    .slice(0, 4)
    .forEach((c) => {
      areaServed.push({ "@type": "City", name: `${c.name}, ${REGION}`, sameAs: cityUrl(c.slug) });
    });

  const business = {
    "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
    "@id": `${url}#business`,
    name: BUSINESS_NAME,
    description: city?.meta_description,
    url,
    telephone: PHONE,
    image: city?.hero_image ? [city.hero_image] : undefined,
    logo: `${origin()}/flamingo.png`,
    priceRange: "$$",
    serviceType: SERVICE_TYPE,
    address: {
      "@type": "PostalAddress",
      addressLocality: city.name,
      addressRegion: REGION,
      addressCountry: COUNTRY,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: HQ_GEO.latitude,
      longitude: HQ_GEO.longitude,
    },
    openingHoursSpecification: HOURS,
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: PHONE,
        contactType: "customer service",
        areaServed: COUNTRY,
        availableLanguage: ["English"],
      },
    ],
    areaServed,
    sameAs: [
      // Reserve for future profiles (Google Business, Facebook, etc.)
    ],
  };

  if (ratingValue && approved.length > 0) {
    business.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue,
      reviewCount: approved.length,
      bestRating: "5",
      worstRating: "1",
    };
    // Include up to 3 individual reviews so rating snippet can render
    business.review = approved.slice(0, 3).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(r.rating),
        bestRating: "5",
        worstRating: "1",
      },
      reviewBody: r.comment,
    }));
  }

  return business;
}

export function buildCityJsonLd({ city, parentCity, allCities, reviews }) {
  if (!city) return null;
  const graph = [
    buildLocalBusiness(city, parentCity, allCities, reviews),
    buildBreadcrumb(city, parentCity),
  ];
  const faq = buildFaqPage(city);
  if (faq) graph.push(faq);
  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

export const BUSINESS_PHONE_DISPLAY = PHONE_DISPLAY;
